import { useEffect } from 'react'
import { useRegistry } from '@/hooks'
import { useSession } from 'next-auth/react'
import { toast } from 'sonner'
import type { Message } from '@ttab/elephant-api/user'
import type { User } from '@/shared/User'
import { AbortError } from '@/shared/types/errors'
import { useTranslation } from 'react-i18next'
import type { TFunction, Namespace } from 'i18next'

export const UserMessagesReceiver = ({ children }: React.PropsWithChildren) => {
  const { user } = useRegistry()
  const { data } = useSession()
  const { t } = useTranslation('errors')

  /**
   * Start polling messages on first load
   */
  useEffect(() => {
    if (!data?.accessToken || !user) {
      return
    }

    let isActive = true
    const abortController = new AbortController()

    const startPolling = async () => {
      let lastId = -1

      while (isActive) {
        lastId = await execPolling(data.accessToken, user, lastId, abortController, t)
      }
    }

    const handleBeforeUnload = () => {
      abortController.abort()
    }

    // Safari/iOS specific: https://bugs.webkit.org/show_bug.cgi?id=219102
    if (/iP(ad|hone|od)|Macintosh/.test(navigator.userAgent) && 'ontouchend' in document) {
      window.addEventListener('unload', handleBeforeUnload)
    } else {
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    void startPolling().catch((ex) => {
      if (ex instanceof AbortError) {
        return
      }
      console.error('Unable to poll messages', ex)
    })

    return () => {
      isActive = false
      abortController.abort()
      if (/iP(ad|hone|od)|Macintosh/.test(navigator.userAgent) && 'ontouchend' in document) {
        window.removeEventListener('unload', handleBeforeUnload)
      } else {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
    }
  }, [data?.accessToken, user, t])

  return (
    <>
      {children}
    </>
  )
}

async function execPolling<Ns extends Namespace>(accessToken: string, user: User, lastId: number, abortController: AbortController, t: TFunction<Ns>): Promise<number> {
  try {
    const res = await user.pollMessages(lastId, accessToken, abortController.signal)

    res.messages.forEach((msg) => {
      displayMessageToast(msg, t)
    })

    return Number(res.lastId)
  } catch (ex) {
    if (ex instanceof AbortError) {
      throw ex
    }
    toast.error(t('systemMessages.connectionFailed'))
    throw ex
  }
}

const displayMessageToast = <Ns extends Namespace>(message: Message, t: TFunction<Ns>) => {
  const msg = { ...message, id: Number(message.id) }

  const desc = [
    `Id: ${message.id}`,
    `Error: ${message.payload.err_message || message.payload.err_name || ''}`
  ]

  if (message.payload.rpc_code) {
    desc.push(`Rpc error: ${message.payload.rpc_code}`)
  }

  if (message.docType) {
    desc.push(`Document type: ${message.docType.replace('core://', '')}`)
  }

  if (message.docUuid) {
    desc.push(`Document UUID: ${message.docUuid}`)
  }

  toast.error(
    t('messages.someError'),
    {
      description: desc.join(' '),
      duration: Infinity,
      closeButton: true,
      action: {
        label: t('messages.copyError'),
        onClick: () => void navigator.clipboard.writeText(JSON.stringify(msg, null, 2))
      }
    }
  )
}
